import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Snackbar,
} from "@mui/material";
import { FaPlus, FaTrash, FaSync } from "react-icons/fa";
import { getAuth } from "firebase/auth";
import { auth } from "../firebase";


const BACKEND = "https://vol-backend.onrender.com";
// Todos los estatus posibles (para mostrar chips en la tabla)
const STATUS_COLORS = {
  enrolled:  { bg: "#e8f5e9", color: "#2e7d32",  label: "Inscrito" },
  completed: { bg: "#e3f2fd", color: "#1565c0",  label: "Completado" },
  disabled:  { bg: "#f5f5f5", color: "#757575",  label: "Deshabilitado" },
  validated: { bg: "#e8f5e9", color: "#1b5e20",  label: "Validado" },
  pending:   { bg: "#fff8e1", color: "#f57f17",  label: "Pendiente" },
  dropped:   { bg: "#fce4ec", color: "#c62828",  label: "Descartado" },
};

// Solo estos se pueden asignar desde Inscripciones
// completed = solo sistema | pending/validated/dropped = Progresos/Docs
const ADMIN_EDITABLE_STATUSES = ["enrolled", "disabled"];

// ── fetch autenticado (sin loops, sin dependencias reactivas) ─
async function apiFetch(path, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error("No hay sesión activa");
  const token = await user.getIdToken();
  const res = await fetch(`${BACKEND}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });
  if (!res.ok) throw new Error(await res.text() || `Error ${res.status}`);
  return res.json();
}

// ── Modal Inscribir ──────────────────────────────────────────
function ModalInscribir({ open, onClose, programs, onSaved }) {
  const [programId, setProgramId] = useState("");
  const [query, setQuery] = useState(""); // ← Cambiado de curp a query
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Validador (CURP, correo o matrícula) ───────────────────
  const [buscando, setBuscando] = useState(false);
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [usuarioNoEncontrado, setUsuarioNoEncontrado] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setProgramId("");
      setQuery("");
      setError(null);
      setUsuarioEncontrado(null);
      setUsuarioNoEncontrado(false);
    }
  }, [open]);

  // ✅ Función de búsqueda (dispara con 3+ caracteres)
  const handleQueryChange = (value) => {
    setQuery(value);
    setUsuarioEncontrado(null);
    setUsuarioNoEncontrado(false);

    if (value.trim().length < 3) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const data = await apiFetch(
          `/admin/inscripciones/buscar-usuario?q=${encodeURIComponent(value.trim())}`
        );
        if (data?.usuario) {
          setUsuarioEncontrado(data.usuario);
          setUsuarioNoEncontrado(false);
        } else {
          setUsuarioEncontrado(null);
          setUsuarioNoEncontrado(true);
        }
      } catch {
        setUsuarioEncontrado(null);
        setUsuarioNoEncontrado(true);
      } finally {
        setBuscando(false);
      }
    }, 400);
  };

  const handleSave = async () => {
    if (!programId) { setError("Selecciona un programa."); return; }
    if (!usuarioEncontrado) { setError("Busca y selecciona un usuario válido."); return; }

    setSaving(true);
    setError(null);
    try {
      await apiFetch("/admin/inscripciones", {
        method: "POST",
        body: JSON.stringify({
          program_id: Number(programId),
          user_id: usuarioEncontrado.uid,
        }),
      });
      onSaved?.("Usuario inscrito correctamente.");
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const nombreCompleto = usuarioEncontrado
    ? [usuarioEncontrado.nombre, usuarioEncontrado.apellido_pat, usuarioEncontrado.apellido_mat]
        .filter(Boolean).join(" ")
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: "1rem", pt: 2.5, px: 3, borderBottom: "1px solid #f0f0f0" }}>
        Inscribir Usuario
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Programa</InputLabel>
          <Select label="Programa" value={programId} onChange={(e) => setProgramId(e.target.value)}>
            {programs.map((p) => (
              <MenuItem key={p.program_id} value={p.program_id}>
                {p.code} — {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ✅ Campo actualizado */}
        <TextField
          label="CURP, correo o matrícula"
          fullWidth
          size="small"
          value={query}
          onChange={(e) => handleQueryChange(e.target.value)}
          helperText="Puedes buscar por CURP, correo electrónico o matrícula."
          InputProps={{
            endAdornment: buscando ? (
              <CircularProgress size={16} sx={{ color: "#aaa" }} />
            ) : null,
          }}
        />

        {/* ── Resultado de validación ── */}
        {usuarioEncontrado && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            mt: 1.5, px: 1.5, py: 1,
            backgroundColor: "#e8f5e9",
            border: "1px solid #a5d6a7",
            borderRadius: 2,
          }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: "50%",
              backgroundColor: "#2e7d32",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1b5e20" }}>
                {nombreCompleto}
              </Typography>
              {usuarioEncontrado.correo && (
                <Typography sx={{ fontSize: "0.72rem", color: "#388e3c" }}>
                  {usuarioEncontrado.correo}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {usuarioNoEncontrado && !buscando && (
          <Box sx={{
            display: "flex", alignItems: "center", gap: 1,
            mt: 1.5, px: 1.5, py: 1,
            backgroundColor: "#fce4ec",
            border: "1px solid #f48fb1",
            borderRadius: 2,
          }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: "50%",
              backgroundColor: "#c62828",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M2 2l6 6M8 2l-6 6" stroke="#fff" strokeWidth="1.8"
                  strokeLinecap="round"/>
              </svg>
            </Box>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#b71c1c" }}>
              Usuario no encontrado
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#777" }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave}
          disabled={saving || buscando || !usuarioEncontrado}  // ← Actualizado
          sx={{
            backgroundColor: "#e60000", color: "#fff",
            textTransform: "none", fontWeight: 700,
            "&:hover": { backgroundColor: "#b30000" },
          }}>
          {saving && <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />}
          Inscribir
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Componente principal ─────────────────────────────────────
export default function Inscripciones() {
  const [enrollments, setEnrollments] = useState([]);
  const [programs,    setPrograms]    = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [filterProgram, setFilterProgram] = useState("");
  const [filterStatus,  setFilterStatus]  = useState("");
  const [search,        setSearch]        = useState("");
  const [filterEstado, setFilterEstado] = useState("");
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const PAGE_SIZE = 50;
  const [modalOpen,     setModalOpen]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmEliminarUsuario, setConfirmEliminarUsuario] = useState(null); // { user_id, nombre, matricula }
  const [eliminandoUsuario, setEliminandoUsuario] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  // Ref para evitar doble carga
  const iniciado = useRef(false);

  // ── Cargar datos (función pura, sin dependencias reactivas) ─
  const cargar = async (progId = "", status = "", q = "") => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (progId)    params.append("program_id", progId);
      if (status)    params.append("status",     status);
      if (q?.trim()) params.append("search",     q.trim());
      const data = await apiFetch(`/admin/inscripciones?${params.toString()}`);
      setEnrollments(data.enrollments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Cargar programas y enrollments al montar — UNA sola vez ─
  useEffect(() => {
    if (iniciado.current) return;
    iniciado.current = true;

    const init = async (user) => {
      if (!user) return;
      try {
        const d = await apiFetch("/admin/inscripciones/programs");
        setPrograms(d.programs || []);
      } catch {}
      await cargar();
    };

    if (auth.currentUser) {
      init(auth.currentUser);
    } else {
      const unsub = auth.onAuthStateChanged((user) => {
        unsub();
        init(user);
      });
    }
  }, []); // eslint-disable-line

  const handleStatusChange = async (userId, programId, newStatus) => {
    try {
      await apiFetch(`/admin/inscripciones/${userId}/${programId}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      setSnack({ open: true, msg: "Estatus actualizado.", severity: "success" });
      cargar(filterProgram, filterStatus, search);
    } catch (err) {
      setSnack({ open: true, msg: `Error: ${err.message}`, severity: "error" });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiFetch(`/admin/inscripciones/${confirmDelete.user_id}/${confirmDelete.program_id}`, { method: "DELETE" });
      setSnack({ open: true, msg: "Inscripción eliminada.", severity: "success" });
      setConfirmDelete(null);
      cargar(filterProgram, filterStatus, search, filterEstado, page);
    } catch (err) {
      setSnack({ open: true, msg: `Error: ${err.message}`, severity: "error" });
    }
  };

  // ── Eliminar usuario completo de la BD ──────────────────────
  const handleEliminarUsuario = async () => {
    if (!confirmEliminarUsuario) return;
    setEliminandoUsuario(true);
    try {
      await apiFetch(`/users/admin/${confirmEliminarUsuario.user_id}`, { method: "DELETE" });
      setSnack({ open: true, msg: `Usuario ${confirmEliminarUsuario.nombre} eliminado permanentemente.`, severity: "success" });
      setConfirmEliminarUsuario(null);
      cargar(filterProgram, filterStatus, search, filterEstado, page);
    } catch (err) {
      setSnack({ open: true, msg: `Error al eliminar: ${err.message}`, severity: "error" });
    } finally {
      setEliminandoUsuario(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#fff", minHeight: "100vh", pb: 6 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: "#111", fontFamily: "'Arial Black', sans-serif", letterSpacing: "-0.5px" }}>
            INSCRIPCIONES
          </Typography>
          <Typography sx={{ color: "#777", fontSize: "0.875rem", mt: 0.3 }}>
            Inscribe usuarios a programas y gestiona su estatus. Solo admins.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<FaPlus size={13} />} onClick={() => setModalOpen(true)}
          sx={{ backgroundColor: "#e60000", color: "#fff", fontWeight: 700, fontSize: "0.85rem", textTransform: "none", px: 2.5, py: 1, borderRadius: 2, boxShadow: "0 4px 12px rgba(230,0,0,0.3)", "&:hover": { backgroundColor: "#b30000" } }}>
          INSCRIBIR USUARIO
        </Button>
      </Box>

      {/* Filtros */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Programa</InputLabel>
          <Select label="Programa" value={filterProgram} onChange={(e) => setFilterProgram(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {programs.map((p) => (
              <MenuItem key={p.program_id} value={p.program_id}>{p.code}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estatus</InputLabel>
          <Select label="Estatus" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(STATUS_COLORS).map(([s, c]) => (
              <MenuItem key={s} value={s}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField size="small" placeholder="Buscar CURP, correo, matrícula, nombre..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }} />

        <Button onClick={() => { setSearch(""); setFilterProgram(""); setFilterStatus(""); cargar(); }} size="small"
          sx={{ textTransform: "none", color: "#777", fontWeight: 600 }}>
          Limpiar
        </Button>

        <Button variant="outlined" size="small" onClick={() => cargar(filterProgram, filterStatus, search)}
          sx={{ textTransform: "none", color: "#e60000", borderColor: "#e60000", fontWeight: 700 }}>
          Buscar
        </Button>

        <Tooltip title="Recargar">
          <IconButton onClick={() => cargar(filterProgram, filterStatus, search)} size="small" sx={{ color: "#e60000" }}>
            <FaSync size={14} />
          </IconButton>
        </Tooltip>

        <Chip label={`${enrollments.length} registros`} size="small"
          sx={{ backgroundColor: "#f5f5f5", fontWeight: 700, fontSize: "0.75rem" }} />
      </Box>

      {/* Tabla */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6, gap: 2 }}>
          <CircularProgress sx={{ color: "#e60000" }} />
          <Typography sx={{ color: "#aaa", fontSize: "0.82rem" }}>Cargando inscripciones...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="warning" sx={{ borderRadius: 2, mb: 2, display: "inline-flex" }}>{error}</Alert>
          <br />
          <Button variant="outlined" onClick={() => cargar(filterProgram, filterStatus, search)}
            startIcon={<FaSync size={13} />}
            sx={{ mt: 1, textTransform: "none", borderColor: "#e60000", color: "#e60000" }}>
            Reintentar
          </Button>
        </Box>
      ) : enrollments.length === 0 ? (
        <Typography sx={{ color: "#aaa", py: 4, textAlign: "center" }}>No se encontraron inscripciones.</Typography>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: 3, overflow: "hidden" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#fafafa" }}>
                {["Nombre completo", "Matrícula", "CURP", "Correo", "Programa", "Estatus", "Cambiar estatus", ""].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: "#444", py: 1.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {enrollments.map((e, idx) => {
                const nombre = [e.nombre, e.apellido_pat, e.apellido_mat].filter(Boolean).join(" ") || "—";
                const si = STATUS_COLORS[e.status] || { bg: "#eee", color: "#333", label: e.status };
                return (
                  <TableRow key={idx} sx={{ "&:hover": { backgroundColor: "#fdf8ff" } }}>
                    <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{nombre}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "#555" }}>{e.matricula || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "#555" }}>{e.curp || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "#555" }}>{e.correo || "—"}</TableCell>
                    <TableCell>
                      <Chip label={e.program_code || "—"} size="small"
                        sx={{ backgroundColor: "#fff0f0", color: "#b30000", fontWeight: 700, fontSize: "0.68rem" }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={si.label} size="small"
                        sx={{ backgroundColor: si.bg, color: si.color, fontWeight: 700, fontSize: "0.7rem" }} />
                    </TableCell>
                    <TableCell>
                      {e.status === "completed" ? (
                        /* completed es intocable — solo lo asigna el sistema */
                        <Chip label="Sistema" size="small"
                          sx={{ fontSize: "0.68rem", color: "#999", backgroundColor: "#f5f5f5", fontStyle: "italic" }} />
                      ) : (
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                          <Select value={e.status}
                            onChange={(ev) => handleStatusChange(e.user_id, e.program_id, ev.target.value)}
                            sx={{ fontSize: "0.78rem" }}>
                            {ADMIN_EDITABLE_STATUSES.map((s) => (
                              <MenuItem key={s} value={s} sx={{ fontSize: "0.78rem" }}>{STATUS_COLORS[s]?.label || s}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Eliminar inscripción">
                        <IconButton size="small"
                          onClick={() => setConfirmDelete({ user_id: e.user_id, program_id: e.program_id, nombre })}
                          sx={{ color: "#ccc", "&:hover": { color: "#e60000" } }}>
                          <FaTrash size={12} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar usuario completo">
                        <IconButton size="small"
                          onClick={() => setConfirmEliminarUsuario({ user_id: e.user_id, nombre, matricula: e.matricula })}
                          sx={{ color: "#ccc", "&:hover": { color: "#b71c1c" }, ml: 0.5 }}>
                          <FaTrash size={12} style={{ color: "#b71c1c" }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ModalInscribir open={modalOpen} onClose={() => setModalOpen(false)} programs={programs}
        onSaved={(msg) => { setSnack({ open: true, msg, severity: "success" }); cargar(); }} />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1rem", pt: 2.5, px: 3 }}>¿Eliminar inscripción?</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography sx={{ fontSize: "0.88rem", color: "#555" }}>
            Se eliminará la inscripción de <strong>{confirmDelete?.nombre}</strong>. Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ textTransform: "none", color: "#777" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleDelete}
            sx={{ backgroundColor: "#e60000", color: "#fff", textTransform: "none", fontWeight: 700, "&:hover": { backgroundColor: "#b30000" } }}>
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Diálogo: Eliminar usuario completo ── */}
      <Dialog open={!!confirmEliminarUsuario} onClose={() => !eliminandoUsuario && setConfirmEliminarUsuario(null)}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: "1rem", pt: 2.5, px: 3, color: "#b71c1c" }}>
          ⚠️ Eliminar usuario permanentemente
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
            Esta acción es IRREVERSIBLE
          </Alert>
          <Typography sx={{ fontSize: "0.88rem", color: "#555", mb: 1 }}>
            Estás a punto de eliminar completamente a:
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#111", mb: 0.5 }}>
            {confirmEliminarUsuario?.nombre}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: "#777", mb: 2 }}>
            Matrícula: {confirmEliminarUsuario?.matricula}
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#c62828" }}>
            Se eliminarán <strong>todos sus datos</strong>: perfil, inscripciones, progreso, documentos y actividad. No se puede recuperar.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setConfirmEliminarUsuario(null)} disabled={eliminandoUsuario}
            sx={{ textTransform: "none", color: "#777" }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleEliminarUsuario} disabled={eliminandoUsuario}
            sx={{ backgroundColor: "#b71c1c", color: "#fff", textTransform: "none", fontWeight: 700,
              "&:hover": { backgroundColor: "#7f0000" } }}>
            {eliminandoUsuario ? <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} /> : null}
            Sí, eliminar usuario
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
